import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import * as ExcelJS from 'exceljs';
import SCUIGenericExcelUpload from '../../../atomic/molecules/SCUIGenericExcelUpload/SCUIGenericExcelUpload';
import { SCUIContent } from '../../../atomic/organisms';
import { StudioFileManagerCommand } from '../../../services/studio/StudioCommand';
import type { LegacyRawImportItemResult } from '../../../types/StudioFileManager';

const MAX_EXCEL_ROWS = 10000;
const BATCH_SIZE = 50;

type ImportKind = 'raw' | 'retouch' | 'unknown';

type ImportRow = {
    rowIndex: number;
    fileName: string;
    filePath: string;
    barcode: string;
    kind: ImportKind;
};

type ImportStats = {
    total: number;
    success: number;
    failed: number;
    raw: number;
    retouch: number;
    unknown: number;
};

type ProgressState = {
    processed: number;
    total: number;
    phase: string;
};

const formatTransferDuration = (ms: number): string => {
    if (!Number.isFinite(ms) || ms < 0) return '0:00';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
};

const cellText = (value: ExcelJS.CellValue): string => {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value)
            .trim()
            .replace(/^["']|["']$/g, '');
    }
    if (typeof value === 'object' && 'text' in (value as any)) {
        return String((value as any).text ?? '').trim();
    }
    if (typeof value === 'object' && 'result' in (value as any)) {
        const result = (value as any).result;
        if (result == null) return '';
        if (typeof result === 'string' || typeof result === 'number' || typeof result === 'boolean') {
            return String(result).trim();
        }
        return '';
    }
    return '';
};

const stripCell = (value: string): string => value.trim().replace(/^["']|["']$/g, '');

/** Excel/CSV bazen barkodu ="868..." veya =868... olarak yazar; baştaki = ve tırnakları temizle. */
const stripBarcode = (value: string): string => {
    let s = value.trim();
    if (s.startsWith('=')) {
        s = s.slice(1).trim();
    }
    return s.replace(/^["']|["']$/g, '').trim();
};

/**
 * Dosya yolundan import tipi:
 * - Siteye_Yuklenecekler / TransferSiteyeYuklenecekler / lcw_fileserver\personel → retouch
 * - igloo_share / TransferIglooShare / igloo-nas → ham kare (raw)
 */
export const detectImportKindFromPath = (filePath: string): ImportKind => {
    const p = (filePath || '').replace(/\//g, '\\').toLowerCase();
    if (!p) {
        return 'unknown';
    }

    if (
        p.includes('siteye_yuklenecekler') ||
        p.includes('transfersiteyeyuklenecekler') ||
        p.includes('lcw_fileserver\\personel')
    ) {
        return 'retouch';
    }

    if (
        p.includes('igloo_share') ||
        p.includes('transferiglooshare') ||
        p.includes('igloo-nas') ||
        p.includes('igloo_nas') ||
        p.includes('\\igloo')
    ) {
        return 'raw';
    }

    // Genel "igloo" (UNC host vb.)
    if (p.includes('igloo')) {
        return 'raw';
    }

    return 'unknown';
};

/** RFC4180-ish CSV satır parse; ; veya , ayırıcı. */
const parseCsvLine = (line: string, delimiter: string): string[] => {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }
        if (ch === delimiter && !inQuotes) {
            cells.push(stripCell(current));
            current = '';
            continue;
        }
        current += ch;
    }
    cells.push(stripCell(current));
    return cells;
};

const detectCsvDelimiter = (sampleLine: string): string => {
    let comma = 0;
    let semicolon = 0;
    let inQuotes = false;
    for (let i = 0; i < sampleLine.length; i++) {
        const ch = sampleLine[i];
        if (ch === '"') {
            inQuotes = !inQuotes;
            continue;
        }
        if (inQuotes) continue;
        if (ch === ',') comma += 1;
        if (ch === ';') semicolon += 1;
    }
    return semicolon > comma ? ';' : ',';
};

const parseImportCsv = async (file: File): Promise<ImportRow[]> => {
    const text = await file.text();
    const lines = text
        .replace(/^\uFEFF/, '')
        .split(/\r\n|\n|\r/)
        .filter((l) => l.trim().length > 0);

    if (lines.length === 0) return [];

    const delimiter = detectCsvDelimiter(lines[0]);
    const rows: ImportRow[] = [];

    for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i], delimiter);
        const fileName = cols[0] ?? '';
        const filePath = cols[1] ?? '';
        const barcode = stripBarcode(cols[2] ?? '');
        if (!fileName && !filePath && !barcode) continue;
        rows.push({
            rowIndex: i + 1,
            fileName,
            filePath,
            barcode,
            kind: detectImportKindFromPath(filePath),
        });
    }
    return rows;
};

const parseImportExcel = async (file: File): Promise<ImportRow[]> => {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
        throw new Error('Worksheet not found');
    }

    const rows: ImportRow[] = [];
    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const fileName = cellText(row.getCell(1).value);
        const filePath = cellText(row.getCell(2).value);
        const barcode = stripBarcode(cellText(row.getCell(3).value));
        if (!fileName && !filePath && !barcode) return;
        rows.push({
            rowIndex: rowNumber,
            fileName,
            filePath,
            barcode,
            kind: detectImportKindFromPath(filePath),
        });
    });
    return rows;
};

const parseImportFile = async (file: File): Promise<ImportRow[]> => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.csv')) {
        return parseImportCsv(file);
    }
    if (name.endsWith('.xlsx')) {
        return parseImportExcel(file);
    }
    if (name.endsWith('.xls')) {
        throw new Error('UNSUPPORTED_XLS');
    }
    throw new Error('UNSUPPORTED_FILE');
};

const mapResult = (r: any): LegacyRawImportItemResult => ({
    rowIndex: r.rowIndex ?? r.RowIndex,
    fileName: r.fileName ?? r.FileName,
    filePath: r.filePath ?? r.FilePath,
    success: Boolean(r.success ?? r.Success),
    error: r.error ?? r.Error,
    barcode: r.barcode ?? r.Barcode,
    fileTransferRequestId: r.fileTransferRequestId ?? r.FileTransferRequestId,
    fileTransferRequestDetailId: r.fileTransferRequestDetailId ?? r.FileTransferRequestDetailId,
    fileServerRetouchRequestId: r.fileServerRetouchRequestId ?? r.FileServerRetouchRequestId,
    fileServerRetouchFileDetailId: r.fileServerRetouchFileDetailId ?? r.FileServerRetouchFileDetailId,
});

const unwrapImportResponse = (response: any): any => {
    if (response == null) return {};
    let cur = response;
    for (let i = 0; i < 3; i++) {
        const nested =
            cur.data ??
            cur.Data ??
            cur.model ??
            cur.Model ??
            cur.result ??
            cur.Result ??
            cur.value ??
            cur.Value ??
            cur.payload ??
            cur.Payload;

        const curResults = cur.results ?? cur.Results;
        const hasNonEmptyResults = Array.isArray(curResults) && curResults.length > 0;
        const hasCounts =
            cur.successCount != null || cur.SuccessCount != null || cur.failedCount != null || cur.FailedCount != null;

        if (nested && typeof nested === 'object' && nested !== cur) {
            const nestedResults = nested.results ?? nested.Results;
            const nestedHasNonEmptyResults = Array.isArray(nestedResults) && nestedResults.length > 0;
            const nestedHasCounts =
                nested.successCount != null ||
                nested.SuccessCount != null ||
                nested.failedCount != null ||
                nested.FailedCount != null;
            // Boş results[] wrapper'ı gerçek payload'u engellemesin
            if (nestedHasNonEmptyResults || (nestedHasCounts && !hasNonEmptyResults)) {
                cur = nested;
                continue;
            }
        }

        if (hasNonEmptyResults || hasCounts) {
            return cur;
        }

        if (!nested || nested === cur || typeof nested !== 'object') break;
        cur = nested;
    }
    return cur;
};

const toPayloadItems = (batch: ImportRow[]) =>
    batch.map((r) => ({
        rowIndex: r.rowIndex,
        fileName: r.fileName,
        filePath: r.filePath,
        barcode: r.barcode,
    }));

const buildSyntheticFailures = (batch: ImportRow[], errorMessage: string): LegacyRawImportItemResult[] =>
    batch.map((r) => ({
        rowIndex: r.rowIndex,
        fileName: r.fileName,
        filePath: r.filePath,
        success: false,
        error: errorMessage,
        barcode: r.barcode ? Number(r.barcode) : undefined,
    }));

const importBatches = async (
    rows: ImportRow[],
    kind: 'raw' | 'retouch',
    onBatchDone?: (processedInKind: number, kindTotal: number) => void,
    fallbackErrorMessage?: string
): Promise<{
    results: LegacyRawImportItemResult[];
    successCount: number;
    failedCount: number;
    createdRequestCount: number;
    createdProductCount: number;
}> => {
    const allResults: LegacyRawImportItemResult[] = [];
    let successCount = 0;
    let failedCount = 0;
    let createdRequestCount = 0;
    let createdProductCount = 0;
    let processedInKind = 0;
    const emptyResultsError = fallbackErrorMessage || 'İçe aktarma yanıtında satır detayı dönmedi.';

    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const environmentPrefix = String(import.meta.env.VITE_ENVIRONMENT ?? '')
            .trim()
            // sonar-disable-next-line S5852
            .replace(/^\/+|\/+$/g, '')
            .toUpperCase();
        const payload = {
            items: toPayloadItems(batch),
            environmentPrefix: environmentPrefix || undefined,
        };
        try {
            const response =
                kind === 'retouch'
                    ? await StudioFileManagerCommand.ImportLegacyRetouchTransfer(payload)
                    : await StudioFileManagerCommand.ImportLegacyRawTransfer(payload);

            const data = unwrapImportResponse(response);
            let batchResults: LegacyRawImportItemResult[] = (data?.results ?? data?.Results ?? []).map(mapResult);

            const apiFailed = Number(data?.failedCount ?? data?.FailedCount ?? 0);
            const apiSuccess = Number(data?.successCount ?? data?.SuccessCount ?? 0);

            // API failedCount dolu ama results boşsa satır bazlı hata üret (tik/mesaj görünsün)
            if (batchResults.length === 0 && (apiFailed > 0 || (apiSuccess === 0 && batch.length > 0))) {
                const transportError =
                    data?.error ?? data?.Error ?? data?.message ?? data?.Message ?? emptyResultsError;
                batchResults = buildSyntheticFailures(batch, String(transportError));
            }

            allResults.push(...batchResults);

            if (batchResults.length > 0) {
                successCount += batchResults.filter((x) => x.success).length;
                failedCount += batchResults.filter((x) => !x.success).length;
            } else {
                successCount += apiSuccess;
                failedCount += apiFailed;
            }

            createdRequestCount += Number(data?.createdRequestCount ?? data?.CreatedRequestCount ?? 0);
            createdProductCount += Number(data?.createdProductCount ?? data?.CreatedProductCount ?? 0);
        } catch (e: any) {
            const msg = e?.message || emptyResultsError;
            const failures = buildSyntheticFailures(batch, msg);
            allResults.push(...failures);
            failedCount += failures.length;
        }

        processedInKind += batch.length;
        onBatchDone?.(processedInKind, rows.length);
    }

    return { results: allResults, successCount, failedCount, createdRequestCount, createdProductCount };
};

export const LegacyTransferImport: React.FC = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [previewRows, setPreviewRows] = useState<ImportRow[]>([]);
    const [results, setResults] = useState<LegacyRawImportItemResult[]>([]);
    const [stats, setStats] = useState<ImportStats | null>(null);
    const [progress, setProgress] = useState<ProgressState | null>(null);
    const [error, setError] = useState('');
    const [transferElapsedMs, setTransferElapsedMs] = useState(0);
    const [lastTransferDurationMs, setLastTransferDurationMs] = useState<number | null>(null);
    const uploadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const uploadStartedAtRef = useRef<number | null>(null);

    const stopTransferTimer = useCallback(() => {
        if (uploadTimerRef.current != null) {
            clearInterval(uploadTimerRef.current);
            uploadTimerRef.current = null;
        }
        if (uploadStartedAtRef.current != null) {
            const duration = Math.round(performance.now() - uploadStartedAtRef.current);
            setLastTransferDurationMs(duration);
            setTransferElapsedMs(duration);
            uploadStartedAtRef.current = null;
        }
    }, []);

    const startTransferTimer = useCallback(() => {
        if (uploadTimerRef.current != null) {
            clearInterval(uploadTimerRef.current);
            uploadTimerRef.current = null;
        }
        uploadStartedAtRef.current = performance.now();
        setTransferElapsedMs(0);
        setLastTransferDurationMs(null);
        uploadTimerRef.current = setInterval(() => {
            if (uploadStartedAtRef.current != null) {
                setTransferElapsedMs(Math.round(performance.now() - uploadStartedAtRef.current));
            }
        }, 250);
    }, []);

    useEffect(
        () => () => {
            if (uploadTimerRef.current != null) {
                clearInterval(uploadTimerRef.current);
            }
        },
        []
    );

    const exampleData = useMemo(
        () => [
            {
                FileName: '20252-W5JO49Z4-CT3_8684689057573_A.jpg',
                FilePath: String.raw`\\igloo-nas\igloo_share\2026 CEKIMLER\01_OCAK\05.01.2026\ornek\20252-W5JO49Z4-CT3_8684689057573_A.jpg`,
                Barcode: '8684689057573',
            },
            {
                FileName: '20252-W5JO49Z4-CT3_8684689057573_A.jpg',
                FilePath: String.raw`\\lcw_fileserver\personel\TEMA\MERCHANDISING\INTERNET_SATIS\GORSELLER\Siteye_Yuklenecekler\2026\ornek\20252-W5JO49Z4-CT3_8684689057573_A.jpg`,
                Barcode: '8684689057573',
            },
        ],
        []
    );

    const progressPercent = useMemo(() => {
        if (!progress || progress.total <= 0) return 0;
        return Math.min(100, Math.round((progress.processed / progress.total) * 100));
    }, [progress]);

    const kindByRowIndex = useMemo(() => {
        const map = new Map<number, ImportKind>();
        previewRows.forEach((r) => map.set(r.rowIndex, r.kind));
        return map;
    }, [previewRows]);

    const onUpload = useCallback(
        async (file: File) => {
            setError('');
            setResults([]);
            setStats(null);
            setProgress(null);
            setLastTransferDurationMs(null);
            setLoading(true);
            startTransferTimer();
            try {
                const rows = await parseImportFile(file);
                if (rows.length === 0) {
                    setError(t('studio.legacyTransferImport.emptyExcel'));
                    setPreviewRows([]);
                    return;
                }
                if (rows.length > MAX_EXCEL_ROWS) {
                    setError(t('studio.legacyTransferImport.maxRowsExceeded', { max: MAX_EXCEL_ROWS }));
                    setPreviewRows([]);
                    return;
                }
                setPreviewRows(rows);

                const rawRows = rows.filter((r) => r.kind === 'raw');
                const retouchRows = rows.filter((r) => r.kind === 'retouch');
                const unknownRows = rows.filter((r) => r.kind === 'unknown');

                if (rawRows.length === 0 && retouchRows.length === 0) {
                    setError(t('studio.legacyTransferImport.unrecognizedPaths'));
                    return;
                }

                const workTotal = rawRows.length + retouchRows.length;
                let workProcessed = 0;
                setProgress({
                    processed: 0,
                    total: workTotal,
                    phase: t('studio.legacyTransferImport.progressStarting'),
                });

                const allResults: LegacyRawImportItemResult[] = [];
                let successCount = 0;
                let failedCount = 0;

                if (rawRows.length > 0) {
                    setProgress({
                        processed: workProcessed,
                        total: workTotal,
                        phase: t('studio.legacyTransferImport.progressRaw'),
                    });
                    const raw = await importBatches(
                        rawRows,
                        'raw',
                        (processedInKind) => {
                            setProgress({
                                processed: workProcessed + processedInKind,
                                total: workTotal,
                                phase: t('studio.legacyTransferImport.progressRaw'),
                            });
                        },
                        t('studio.legacyTransferImport.noRowDetails')
                    );
                    workProcessed += rawRows.length;
                    allResults.push(...raw.results);
                    successCount += raw.successCount;
                    failedCount += raw.failedCount;
                }

                if (retouchRows.length > 0) {
                    setProgress({
                        processed: workProcessed,
                        total: workTotal,
                        phase: t('studio.legacyTransferImport.progressRetouch'),
                    });
                    const retouch = await importBatches(
                        retouchRows,
                        'retouch',
                        (processedInKind) => {
                            setProgress({
                                processed: workProcessed + processedInKind,
                                total: workTotal,
                                phase: t('studio.legacyTransferImport.progressRetouch'),
                            });
                        },
                        t('studio.legacyTransferImport.noRowDetails')
                    );
                    workProcessed += retouchRows.length;
                    allResults.push(...retouch.results);
                    successCount += retouch.successCount;
                    failedCount += retouch.failedCount;
                }

                for (const u of unknownRows) {
                    allResults.push({
                        rowIndex: u.rowIndex,
                        fileName: u.fileName,
                        filePath: u.filePath,
                        success: false,
                        error: t('studio.legacyTransferImport.unrecognizedPathRow'),
                        barcode: undefined,
                    });
                    failedCount += 1;
                }

                allResults.sort((a, b) => a.rowIndex - b.rowIndex);
                setResults(allResults);

                // Özet: sonuç satırlarından hesapla; yoksa API sayaçlarını kullan
                const successFromResults = allResults.filter((r) => r.success).length;
                const failedFromResults = allResults.filter((r) => !r.success).length;
                const finalSuccess = allResults.length > 0 ? successFromResults : successCount;
                const finalFailed = allResults.length > 0 ? failedFromResults : failedCount;

                setStats({
                    total: rows.length,
                    success: finalSuccess,
                    failed: finalFailed,
                    raw: rawRows.length,
                    retouch: retouchRows.length,
                    unknown: unknownRows.length,
                });
                setProgress({
                    processed: workTotal,
                    total: workTotal,
                    phase: t('studio.legacyTransferImport.progressDone'),
                });
            } catch (e: any) {
                const msg = e?.message || '';
                if (msg === 'UNSUPPORTED_XLS' || msg === 'UNSUPPORTED_FILE' || msg.includes('central directory')) {
                    setError(t('studio.legacyTransferImport.invalidFileType'));
                } else {
                    setError(msg || t('studio.legacyTransferImport.importFailed'));
                }
            } finally {
                stopTransferTimer();
                setLoading(false);
            }
        },
        [t, startTransferTimer, stopTransferTimer]
    );

    const clearAll = () => {
        setResults([]);
        setStats(null);
        setProgress(null);
        setPreviewRows([]);
        setError('');
        setTransferElapsedMs(0);
        setLastTransferDurationMs(null);
    };

    const showTimer = loading || lastTransferDurationMs !== null;

    return (
        <SCUIContent headerName={t('studio.legacyTransferImport.title')}>
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <UploadFileIcon color="primary" fontSize="large" />
                    <Typography variant="body1" color="text.secondary">
                        {t('studio.legacyTransferImport.subtitle')}
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                    {t('studio.legacyTransferImport.autoDetectHint', {
                        max: MAX_EXCEL_ROWS,
                        batch: BATCH_SIZE,
                    })}
                </Alert>

                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('studio.legacyTransferImport.stagingHint')}
                </Alert>

                <SCUIGenericExcelUpload
                    onUpload={onUpload}
                    accept=".xlsx,.csv"
                    loading={loading}
                    disabled={loading}
                    showExample
                    exampleData={exampleData}
                    exampleFileName="legacy-transfer-import-template.xlsx"
                    title={t('studio.legacyTransferImport.uploadTitle')}
                    description={t('studio.legacyTransferImport.uploadDescription')}
                />

                {(loading || progress) && (
                    <Paper sx={{ mt: 2, p: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                mb: 1,
                            }}
                        >
                            <Typography variant="subtitle2">
                                {loading
                                    ? progress?.phase || t('studio.legacyTransferImport.progressStarting')
                                    : t('studio.legacyTransferImport.progressDone')}
                            </Typography>
                            {showTimer && (
                                <Tooltip
                                    title={
                                        loading
                                            ? t('studio.legacyTransferImport.transferElapsed', {
                                                  time: formatTransferDuration(transferElapsedMs),
                                              })
                                            : t('studio.legacyTransferImport.transferTotalDuration', {
                                                  time: formatTransferDuration(lastTransferDurationMs ?? 0),
                                              })
                                    }
                                    arrow
                                    placement="top"
                                >
                                    <Chip
                                        icon={
                                            loading ? (
                                                <HourglassEmptyOutlinedIcon
                                                    sx={{ fontSize: 18, color: 'warning.main' }}
                                                />
                                            ) : (
                                                <AccessTimeIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                            )
                                        }
                                        label={formatTransferDuration(
                                            loading ? transferElapsedMs : (lastTransferDurationMs ?? 0)
                                        )}
                                        size="small"
                                        sx={(theme) => ({
                                            fontWeight: 700,
                                            fontVariantNumeric: 'tabular-nums',
                                            bgcolor: loading
                                                ? alpha(theme.palette.warning.main, 0.14)
                                                : alpha(theme.palette.success.main, 0.14),
                                            border: '1px solid',
                                            borderColor: loading
                                                ? theme.palette.warning.light
                                                : theme.palette.success.light,
                                            '& .MuiChip-icon': { ml: '4px' },
                                        })}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        <LinearProgress
                            variant={progress && progress.total > 0 ? 'determinate' : 'indeterminate'}
                            value={progressPercent}
                            sx={{ height: 10, borderRadius: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {progress && progress.total > 0
                                ? t('studio.legacyTransferImport.progressDetail', {
                                      processed: progress.processed,
                                      total: progress.total,
                                      percent: progressPercent,
                                  })
                                : t('studio.legacyTransferImport.progressPreparing')}
                        </Typography>
                    </Paper>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {stats && (
                    <Paper sx={{ mt: 2, p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                            {t('studio.legacyTransferImport.statsTitle')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
                            <Chip
                                icon={<SummarizeIcon />}
                                color="default"
                                variant="outlined"
                                label={t('studio.legacyTransferImport.statsTotal', { count: stats.total })}
                            />
                            <Chip
                                icon={<CheckCircleOutlineIcon />}
                                color="success"
                                label={t('studio.legacyTransferImport.statsSuccess', { count: stats.success })}
                            />
                            <Chip
                                icon={<ErrorOutlineIcon />}
                                color="error"
                                label={t('studio.legacyTransferImport.statsFailed', { count: stats.failed })}
                            />
                            <Chip
                                variant="outlined"
                                label={t('studio.legacyTransferImport.statsRaw', { count: stats.raw })}
                            />
                            <Chip
                                variant="outlined"
                                label={t('studio.legacyTransferImport.statsRetouch', { count: stats.retouch })}
                            />
                            {stats.unknown > 0 && (
                                <Chip
                                    color="warning"
                                    variant="outlined"
                                    label={t('studio.legacyTransferImport.statsUnknown', { count: stats.unknown })}
                                />
                            )}
                        </Stack>
                    </Paper>
                )}

                {previewRows.length > 0 && results.length === 0 && (
                    <Paper sx={{ mt: 2, p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            {t('studio.legacyTransferImport.previewTitle', { count: previewRows.length })}
                        </Typography>
                        <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                            {previewRows.slice(0, 50).map((r) => (
                                <Typography key={r.rowIndex} variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    #{r.rowIndex}: [{r.kind}] {r.fileName} / {r.barcode}
                                </Typography>
                            ))}
                            {previewRows.length > 50 && (
                                <Typography variant="caption">
                                    {t('studio.legacyTransferImport.previewMore', {
                                        count: previewRows.length - 50,
                                    })}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                )}

                {results.length > 0 && (
                    <Paper sx={{ mt: 2, p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            {t('studio.legacyTransferImport.resultsTitle')}
                        </Typography>
                        <Box sx={{ maxHeight: 420, overflow: 'auto', mb: 2 }}>
                            {results.map((r) => (
                                <Box
                                    key={`${r.rowIndex}-${r.fileName}`}
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        alignItems: 'flex-start',
                                        mb: 1,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        pb: 1,
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            r.success
                                                ? t('studio.legacyTransferImport.statusSuccess')
                                                : t('studio.legacyTransferImport.statusFailed')
                                        }
                                        arrow
                                    >
                                        <Box sx={{ pt: 0.25, lineHeight: 0 }}>
                                            {r.success ? (
                                                <CheckCircleIcon color="success" fontSize="small" />
                                            ) : (
                                                <CancelIcon color="error" fontSize="small" />
                                            )}
                                        </Box>
                                    </Tooltip>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            #{r.rowIndex} [{kindByRowIndex.get(r.rowIndex) ?? '-'}] {r.fileName}
                                        </Typography>
                                        {!r.success && (
                                            <Typography variant="caption" color="error">
                                                {r.error || t('studio.legacyTransferImport.importFailed')}
                                            </Typography>
                                        )}
                                        {r.success && (
                                            <Typography variant="caption" color="text.secondary">
                                                {t('studio.legacyTransferImport.resultMeta', {
                                                    barcode: r.barcode ?? '-',
                                                    requestId:
                                                        r.fileServerRetouchRequestId ?? r.fileTransferRequestId ?? '-',
                                                    detailId:
                                                        r.fileServerRetouchFileDetailId ??
                                                        r.fileTransferRequestDetailId ??
                                                        '-',
                                                })}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Button variant="outlined" onClick={clearAll}>
                            {t('studio.legacyTransferImport.clearResults')}
                        </Button>
                    </Paper>
                )}
            </Box>
        </SCUIContent>
    );
};

export default LegacyTransferImport;
